import React, { createContext, useContext, ReactNode } from 'react';
import { useNotifications } from '@/hooks/use-notifications';
import { tokenStorage } from '@/utils/token-storage';
import { NotificationData } from '@/types/notification';

// Define the context interface
interface NotificationContextType {
    notifications: NotificationData[];
    unreadCount: number;
    isConnected: boolean;
    isLoading: boolean;
    error: string | null;
    // Actions
    markAsRead: (notificationId: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    refetchNotifications: () => Promise<void>;
    connectWebSocket: () => Promise<void>;
    disconnectWebSocket: () => void;
}

// Create the context
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
    children: ReactNode;
    getToken?: () => string | null;
    autoConnect?: boolean;
    maxNotifications?: number;
}

// Provider component
export const NotificationProvider: React.FC<NotificationProviderProps> = ({
    children,
    getToken = tokenStorage.getToken,
    autoConnect = true,
    maxNotifications = 50
}) => {
    // Use the existing hook - this will be the single source of truth
    const notificationState = useNotifications({
        getToken,
        autoConnect,
        maxNotifications
    });

    // Return the notification state directly
    const contextValue: NotificationContextType = {
        ...notificationState
    };

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}
        </NotificationContext.Provider>
    );
};

// Custom hook to use the notification context
export const useNotificationContext = (): NotificationContextType => {
    const context = useContext(NotificationContext);

    if (!context) {
        throw new Error('useNotificationContext must be used within a NotificationProvider');
    }

    return context;
};

// Export the context for advanced usage
export { NotificationContext };